import arrowheadUpOutline from '@iconify/icons-eva/arrowhead-up-outline';
import personRemoveOutline from '@iconify/icons-eva/person-remove-outline';
import plusFill from '@iconify/icons-eva/plus-fill';
import { Icon } from '@iconify/react';
import {
  Button,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import 'react-confirm-alert/src/react-confirm-alert.css';
import useStore from '@/store/use-store';
import { fStringCapitalizeFirstLetter } from '@/utils/formatString.js';
import arrowheadDownOutline from '@iconify/icons-eva/arrowhead-down-outline';
import useConfirm from '../../../hooks/useConfirm';
import InviteMemberModal from './InviteUserModal';

OrganizationMembers.propTypes = {
  organization: PropTypes.object
};

export default function OrganizationMembers({ organization }) {
  const [localOrganization, setLocalOrganization] = useState(organization);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { removeUser, promoteUser, demoteUser } = useStore((state) => state);
  const user = useStore((state) => state.user);

  const handleRemoveMember = (memberId) => {
    confirm({
      title: t('invitation.confirmation.remove_title'),
      description: t('invitation.confirmation.remove'),
      confirmationText: t('remove')
    })
      .then(() => {
        removeUser(memberId)
          .then((res) => {
            const { members } = res;
            const updatedOrganization = { ...localOrganization, members };
            setLocalOrganization(updatedOrganization);
            toast.success(t('organization.removed_user'));
          })
          .catch((error) => console.log(error));
      })
      .catch(() => {});
  };

  const handlePromoteMember = (memberId) => {
    confirm({
      title: t('organization.confirmation.promote_title'),
      description: t('organization.confirmation.promote'),
      confirmationText: t('promote')
    })
      .then(() => {
        promoteUser(memberId)
          .then((res) => {
            const { members } = res;
            const updatedOrganization = { ...localOrganization, members };
            setLocalOrganization(updatedOrganization);
            toast.success(t('organization.promoted_user'));
          })
          .catch(() => toast.error(t('organization.promote_user_error')));
      })
      .catch(() => {});
  };

  const handleDemoteMember = (memberId) => {
    confirm({
      title: t('organization.confirmation.demote_title'),
      description: t('organization.confirmation.demote'),
      confirmationText: t('demote')
    })
      .then(() => {
        demoteUser(memberId)
          .then((res) => {
            const { members } = res;
            const updatedOrganization = { ...localOrganization, members };
            setLocalOrganization(updatedOrganization);
            toast.success(t('organization.demoted_user'));
          })
          .catch(() => toast.error(t('organization.demote_user_error')));
      })
      .catch(() => {});
  };

  const isAdmin = localOrganization?.isAdmin;
  return (
    <>
      {isAdmin && (
        <InviteMemberModal
          isOpen={modalIsOpen}
          close={() => setModalIsOpen(false)}
          organization={localOrganization}
        />
      )}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mt={5} mb={5}>
        <Typography variant="h4" gutterBottom>
          {fStringCapitalizeFirstLetter(t('members'))}
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<Icon icon={plusFill} />}
            onClick={() => setModalIsOpen(true)}
          >
            {t('invite_member')}
          </Button>
        )}
      </Stack>
      <Grid container spacing={3} padding={3}>
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{fStringCapitalizeFirstLetter(t('name'))}</TableCell>
                  <TableCell>{fStringCapitalizeFirstLetter(t('email'))}</TableCell>
                  <TableCell>{fStringCapitalizeFirstLetter(t('role'))}</TableCell>
                  {isAdmin && <TableCell>{fStringCapitalizeFirstLetter(t('actions'))}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {localOrganization.members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      {member.user.firstName} {member.user.lastName}
                    </TableCell>
                    <TableCell>{member.user.email}</TableCell>
                    <TableCell>{member.roles.includes('ROLE_ADMIN') ? 'Admin' : 'User'}</TableCell>
                    {isAdmin && user.id !== member.user.id && (
                      <TableCell>
                        <IconButton onClick={() => handleRemoveMember(member.id)}>
                          <Icon icon={personRemoveOutline} />
                        </IconButton>
                        {member.roles.includes('ROLE_ADMIN') ? (
                          <IconButton onClick={() => handleDemoteMember(member.id)}>
                            <Icon icon={arrowheadDownOutline} />
                          </IconButton>
                        ) : (
                          <IconButton onClick={() => handlePromoteMember(member.id)}>
                            <Icon icon={arrowheadUpOutline} />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </>
  );
}
